import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';
import { getResponseData } from '../models/api-response.model';
import { Category } from '../models/category.model';

@Injectable({
  providedIn: 'root',
})
export class CategoriesService {
  private readonly platform = (environment as { platform?: number }).platform ?? 0;

  constructor(private api: ApiService) {}

  /** Top-level categories only, in dashboard `SortOrder` (same order as `GetCategoryTree` roots). */
  getCategories(categoryGroupId?: number, platform?: number): Observable<Category[]> {
    return this.getCategoryTree(categoryGroupId, platform);
  }

  /**
   * @param categoryGroupId Optional filter by category group.
   * @param platform When set, overrides `environment.platform` (0 = Store, 1 = Electronics per API).
   */
  getCategoryTree(categoryGroupId?: number, platform?: number): Observable<Category[]> {
    const resolvedPlatform =
      typeof platform === 'number' && Number.isFinite(platform) ? platform : this.platform;
    const params: Record<string, string | number | boolean> = { platform: resolvedPlatform };
    if (typeof categoryGroupId === 'number' && Number.isFinite(categoryGroupId) && categoryGroupId > 0) {
      params['categoryGroupId'] = categoryGroupId;
    }

    return this.api
      .get<Category[]>('Categories/GetCategoryTree', params)
      .pipe(
        map((response) => {
          const data = getResponseData(response, []);
          if (!Array.isArray(data)) {
            return [];
          }
          return data.map((node) => this.normalizeCategory(node as unknown as Record<string, unknown>));
        })
      );
  }

  private normalizeCategory(raw: Record<string, unknown>): Category {
    const childrenRaw = (raw['children'] ?? raw['Children'] ?? []) as unknown[];
    return {
      id: Number(raw['id'] ?? raw['Id'] ?? 0),
      name: String(raw['name'] ?? raw['Name'] ?? ''),
      nameEn: (raw['nameEn'] ?? raw['NameEn'] ?? null) as string | null,
      nameAr: (raw['nameAr'] ?? raw['NameAr'] ?? null) as string | null,
      description: (raw['description'] ?? raw['Description'] ?? null) as string | null,
      imageUrl: (raw['imageUrl'] ?? raw['ImageUrl'] ?? null) as string | null,
      categoryGroupId: Number(raw['categoryGroupId'] ?? raw['CategoryGroupId'] ?? 0),
      parentCategoryId: (raw['parentCategoryId'] ?? raw['ParentCategoryId'] ?? null) as number | null,
      sortOrder: Number(raw['sortOrder'] ?? raw['SortOrder'] ?? 0) || undefined,
      showOnNavigation: this.readOptionalBool(raw, 'showOnNavigation', 'ShowOnNavigation'),
      navigationSortOrder: this.readOptionalNumber(raw, 'navigationSortOrder', 'NavigationSortOrder'),
      directProductCount: Number(raw['directProductCount'] ?? raw['DirectProductCount'] ?? 0),
      totalProductCount: Number(raw['totalProductCount'] ?? raw['TotalProductCount'] ?? 0),
      children: Array.isArray(childrenRaw)
        ? childrenRaw.map((child) => this.normalizeCategory(child as Record<string, unknown>))
        : [],
    };
  }

  private readOptionalBool(raw: Record<string, unknown>, camel: string, pascal: string): boolean | undefined {
    const value = raw[camel] ?? raw[pascal];
    if (value === undefined || value === null || value === '') {
      return undefined;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value !== 0;
    }
    if (typeof value === 'string') {
      const lowered = value.trim().toLowerCase();
      if (lowered === 'true' || lowered === '1') return true;
      if (lowered === 'false' || lowered === '0') return false;
    }
    return undefined;
  }

  private readOptionalNumber(raw: Record<string, unknown>, camel: string, pascal: string): number | undefined {
    const value = raw[camel] ?? raw[pascal];
    if (value === undefined || value === null || value === '') {
      return undefined;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
}
